// Pure native JS: open/close chat container via floating trigger with fade animation

(function() {
    'use strict';

    // Keep CSS --app-height in sync with visual viewport (fix mobile address-bar jump)
    // Use visualViewport and rAF to update during scrolling/touch for immediate response.
    let _rafId = null;
    function setAppHeight() {
        const height = window.visualViewport ? window.visualViewport.height : window.innerHeight;
        document.documentElement.style.setProperty('--app-height', `${height}px`);
    }

    // Throttled schedule so we don't spam layout while still updating continuously during scroll
    function scheduleAppHeight() {
        if (_rafId) return;
        _rafId = requestAnimationFrame(() => {
            setAppHeight();
            _rafId = null;
        });
    }

    // Initial set
    setAppHeight();

    // Prefer visualViewport events (fires during address-bar hide/show and scrolling)
    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', setAppHeight);
        window.visualViewport.addEventListener('scroll', scheduleAppHeight);
    } else {
        // Fallback
        window.addEventListener('resize', setAppHeight);
        window.addEventListener('scroll', scheduleAppHeight, { passive: true });
        window.addEventListener('touchmove', scheduleAppHeight, { passive: true });
    }

    // Media query for "mobile" breakpoint (< 48em)
    const mq = window.matchMedia('(max-width: 48em)');

    // Helper to check whether we should apply scroll lock (only on small screens)
    function isSmallScreen() {
        try {
            return mq.matches;
        } catch (e) {
            // If matchMedia unsupported, fallback to width check
            return window.innerWidth <= (48 * parseFloat(getComputedStyle(document.documentElement).fontSize || 16));
        }
    }

    // Scroll lock state and restore helpers
    let lockedScrollY = 0;
    let scrollLocked = false;
    const originalBodyStyles = {
        position: '',
        top: '',
        left: '',
        right: '',
        overflow: '',
        width: ''
    };

    // Lock page scroll (preserve scroll position) — useful on iOS/Android to prevent address bar hiding by scroll
    function lockScroll() {
        if (scrollLocked) return;
        // Only lock on small screens
        if (!isSmallScreen()) return;

        // Save current scroll position
        lockedScrollY = window.scrollY || window.pageYOffset || 0;

        // Save original inline styles to restore later
        const bodyStyle = document.body.style;
        originalBodyStyles.position = bodyStyle.position || '';
        originalBodyStyles.top = bodyStyle.top || '';
        originalBodyStyles.left = bodyStyle.left || '';
        originalBodyStyles.right = bodyStyle.right || '';
        originalBodyStyles.overflow = bodyStyle.overflow || '';
        originalBodyStyles.width = bodyStyle.width || '';

        // Apply fixed positioning to prevent the viewport from changing when user scrolls
        bodyStyle.position = 'fixed';
        bodyStyle.top = `-${lockedScrollY}px`;
        bodyStyle.left = '0';
        bodyStyle.right = '0';
        bodyStyle.overflow = 'hidden';
        // Ensure width remains 100% to avoid horizontal overflow
        bodyStyle.width = '100%';

        scrollLocked = true;
    }

    // Unlock page scroll and restore previous scroll position and styles
    function unlockScroll() {
        if (!scrollLocked) return;
        const bodyStyle = document.body.style;

        // Restore original inline styles
        bodyStyle.position = originalBodyStyles.position;
        bodyStyle.top = originalBodyStyles.top;
        bodyStyle.left = originalBodyStyles.left;
        bodyStyle.right = originalBodyStyles.right;
        bodyStyle.overflow = originalBodyStyles.overflow;
        bodyStyle.width = originalBodyStyles.width;

        // Restore scroll position
        window.scrollTo(0, lockedScrollY);
        lockedScrollY = 0;
        scrollLocked = false;
    }

    // If the viewport becomes larger while scroll is locked, unlock to avoid stuck state
    function handleMqChange(e) {
        if (!e.matches && scrollLocked) {
            // screen is now larger than 48em
            unlockScroll();
        }
        // When going to smaller screens we don't auto-lock; lock occurs on open
    }

    // Attach media query change listener (use both modern and legacy APIs)
    if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', handleMqChange);
    } else if (typeof mq.addListener === 'function') {
        mq.addListener(handleMqChange);
    }

    // Configurable selectors
    const TRIGGER_ID = 'sam-chatbot-trigger';
    const CONTAINER_ID = 'sam-chatbot-container';
    const MINIMIZE_ID = 'sam-chatbot-minimize';
    const VISIBLE_CLASS = 'sam-chatbot-visible';
    const ANIMATION_DURATION = 300; // ms (match CSS transition)

    // Get elements
    const trigger = document.getElementById(TRIGGER_ID);
    const container = document.getElementById(CONTAINER_ID);
    const minimizeBtn = document.getElementById(MINIMIZE_ID);
    const form = document.getElementById('sam-chatbot-form');
    const textarea = document.getElementById('sam-chatbot-input');

    if (!trigger || !container) return;

    // Show typing indicator at the bottom
    function showTyping() {
        const messagesDiv = document.getElementById('sam-chatbot-messages');
        if (!messagesDiv) return;
        // Remove existing typing indicator if present
        hideTyping();
        const typingDiv = document.createElement('div');
        typingDiv.id = 'sam-chatbot-typing';
        typingDiv.className = 'sam-chatbot-message sam-chatbot-message-bot';
        typingDiv.innerHTML = `
                <div class="sam-chatbot-typing-indicator">
                    <span></span><span></span><span></span>
                </div>
                <span class="sr-only">Sam is typing...</span>
        `;
        messagesDiv.appendChild(typingDiv);
        messagesDiv.scrollTo({ top: messagesDiv.scrollHeight, behavior: 'auto' });
    }

    // Hide typing indicator
    function hideTyping() {
        const typingDiv = document.getElementById('sam-chatbot-typing');
        if (typingDiv && typingDiv.parentNode) {
            typingDiv.parentNode.removeChild(typingDiv);
        }
    }

    function appendMessage(text, sender = 'user', restore = false) {
        const messagesDiv = document.getElementById('sam-chatbot-messages');
        if (!messagesDiv) return;

        const now = new Date();
        const hh = String(now.getHours()).padStart(2, '0');
        const mm = String(now.getMinutes()).padStart(2, '0');
        const time = `${hh}:${mm}`;

        const div = document.createElement('div');
        div.className = 'sam-chatbot-message' + (sender === 'bot' ? ' sam-chatbot-message-bot' : '');

        let messageHtml;
        if (sender === 'user') {
            messageHtml = `<span class="sam-chatbot-message-text">${text}</span>
            <span class="sam-chatbot-message-time"><span>Ontvangen</span> ${time}</span>`;
        } else {
            messageHtml = `<span class="sam-chatbot-message-text">${text}</span>
            <span class="sam-chatbot-message-time">${time}</span>`;
        }

        div.innerHTML = messageHtml;
        messagesDiv.appendChild(div);
        // After messagesDiv.appendChild(div);
        messagesDiv.scrollTo({ top: messagesDiv.scrollHeight, behavior: restore ? 'auto' : 'smooth' });

        // Initialize any dynamic TTS blocks that may be inside the appended message
        // (the SAM chatbot can receive HTML containing the [google_tts] shortcode output).
        try {
            if (typeof window !== 'undefined' && typeof window.initTTSBlocks === 'function') {
                window.initTTSBlocks(div);
            } else {
                // Retry shortly in case the TTS script hasn't initialized yet
                setTimeout(() => {
                    if (typeof window.initTTSBlocks === 'function') window.initTTSBlocks(div);
                }, 250);
            }
        } catch (err) {
            // Non-fatal; continue gracefully
            // eslint-disable-next-line no-console
            console.warn('initTTSBlocks failed', err);
        }

        // Update session storage (store time too)
        if (restore) return; // Don't duplicate on restore
        let history = JSON.parse(sessionStorage.getItem('samChatSession') || '[]');
        history.push({ sender, text, time });
        sessionStorage.setItem('samChatSession', JSON.stringify(history));
    }

    // Helper functions
    function openChat() {
        // Ensure CSS var is up-to-date before showing so there's no jump
        setAppHeight();

        // Lock scroll to prevent the address bar hiding/showing when user scrolls
        // Only lock on small screens
        if (isSmallScreen()) {
            lockScroll();
        }

        container.hidden = false;
        container.style.display = 'flex';
        // Force reflow to ensure transition triggers
        void container.offsetWidth;
        container.classList.add(VISIBLE_CLASS);
        trigger.setAttribute('aria-expanded', 'true');
        trigger.setAttribute('aria-label', (window.samChatbot?.messages?.close) || 'Sluit chatbot');

        const messagesDiv = document.getElementById('sam-chatbot-messages');
        let chatSession = sessionStorage.getItem('samChatSession');

        if (!chatSession) {
            // No session: fetch welcome message via WP AJAX
            showTyping();

            let timedOut = false;
            const timeoutId = setTimeout(() => {
                timedOut = true;
                hideTyping();
                appendMessage('Sorry, het duurde te lang om te laden.', 'bot');
            }, 10000); // 10 seconds

            fetch(tts_vars.ajaxurl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'sam_get_welcome_message',
                    nonce: tts_vars.nonce
                })
            })
                .then(response => response.json())
                .then(data => {
                    if (timedOut) return;
                    clearTimeout(timeoutId);
                    hideTyping();
                    if (data.success && data.data) {
                        appendMessage(data.data.message, 'bot');
                        sessionStorage.setItem('samChatSession', JSON.stringify([{
                            sender: 'bot',
                            text: data.data.message
                        }]));
                    } else {
                        appendMessage('Sorry, er ging iets mis.', 'bot');
                    }
                })
                .catch(() => {
                    if (timedOut) return;
                    clearTimeout(timeoutId);
                    hideTyping();
                    appendMessage('Sorry, er ging iets mis.', 'bot');
                });
        } else {
            // Session exists: render chat history only if messagesDiv is empty
            const hasMessages = messagesDiv && messagesDiv.querySelector('.sam-chatbot-message');
            if (!hasMessages) {
                showTyping();
                const history = JSON.parse(chatSession);
                history.forEach(msg => {
                    appendMessage(msg.text, msg.sender, true);
                });
                hideTyping();
            } else if (messagesDiv) {
                // Already has messages, just ensure scroll is at bottom
                messagesDiv.scrollTo({ top: messagesDiv.scrollHeight, behavior: 'auto' });
            }
        }
    }

    function closeChat() {
        // Remove visible class for fade-out
        container.classList.remove(VISIBLE_CLASS);

        // Wait for animation to complete before hiding and unlocking scroll
        setTimeout(() => {
            container.hidden = true;
            container.style.display = 'none';
            // Unlock scroll AFTER the container is hidden so layout restoration happens smoothly
            unlockScroll();
        }, ANIMATION_DURATION);

        // ARIA state
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-label', (window.samChatbot?.messages?.open) || 'Open chatbot');
        trigger.focus();
    }

    function isOpen() {
        return container.classList.contains(VISIBLE_CLASS);
    }

    function toggleChat() {
        if (isOpen()) {
            closeChat();
        } else {
            openChat();
        }
    }

    // Click handler on floating trigger
    trigger.addEventListener('click', function(e) {
        e.preventDefault();
        toggleChat();
    });

    // Click handler on minimize button
    if (minimizeBtn) {
        minimizeBtn.addEventListener('click', function(e) {
            e.preventDefault();
            closeChat();
        });

        // Keyboard accessibility for minimize button
        minimizeBtn.addEventListener('keydown', function(e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                closeChat();
            }
        });
    }

    // Global click handler for elements with .open-sam class
    document.addEventListener('click', function(e) {
        const openBtn = e.target.closest && e.target.closest('.open-sam');
        if (!openBtn) return;
        e.preventDefault();
        if (typeof openChat === 'function') {
            openChat();
        } else if (window.samChatToggle && typeof window.samChatToggle.open === 'function') {
            window.samChatToggle.open();
        }
    });

    // Optional: Escape to close when open
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && isOpen()) {
            closeChat();
        }
    });

    // Optional: click outside to close
    // document.addEventListener('click', function(e) {
    //     if (!isOpen()) return;
    //     if (!container.contains(e.target) && e.target !== trigger && !trigger.contains(e.target)) {
    //         closeChat();
    //     }
    // });

    // Initialize default hidden state
    if (!container.classList.contains(VISIBLE_CLASS)) {
        container.hidden = true;
        container.style.display = 'none';
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-label', (window.samChatbot?.messages?.open) || 'Open chatbot');
    }

    // When opening the chat ensure the textarea is visible on mobile and gets focus
    const originalOpen = window.samChatToggle?.open;
    window.samChatToggle = window.samChatToggle || {};
    window.samChatToggle.open = function() {
        setAppHeight();
        if (typeof originalOpen === 'function') originalOpen();
        // small timeout to allow the opening animation to start
        setTimeout(() => {
            const ta = document.getElementById('sam-chatbot-input');
            if (ta) {
                try { ta.focus({preventScroll: true}); } catch (e) { ta.focus(); }
                ta.scrollIntoView({ block: 'end', behavior: 'smooth' });
            }
        }, 250);
    };

    if (form && textarea) {
        textarea.addEventListener('keydown', function(e) {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
            }
            // Shift+Enter: do nothing, allow newline
        });
        form.addEventListener('submit', function(e) {
            e.preventDefault();

            const message = textarea.value.trim();
            if (!message) return;

            appendMessage(message, 'user');
            showTyping();

            // Disable input and send button
            textarea.disabled = true;
            const sendBtn = document.getElementById('sam-chatbot-send');
            if (sendBtn) sendBtn.disabled = true;

            // Timeout to re-enable form after 30s
            const enableForm = () => {
                textarea.disabled = false;
                if (sendBtn) sendBtn.disabled = false;
            };
            const timeoutId = setTimeout(() => {
                hideTyping();
                enableForm();
            }, 30000); // 30 seconds

            // Prepare data
            const conversationId = sessionStorage.getItem('samConversationId') || crypto.randomUUID();
            sessionStorage.setItem('samConversationId', conversationId);

            fetch(tts_vars.ajaxurl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'sam_stream_message',
                    nonce: tts_vars.nonce,
                    message: message,
                    conversation_id: conversationId
                })
            })
                .then(res => res.json())
                .then(data => {
                    clearTimeout(timeoutId);
                    hideTyping();
                    enableForm();
                    if (data.success && data.data && data.data.html) {
                        appendMessage(data.data.html, 'bot');
                    } else {
                        // Error fallback
                        appendMessage('Sorry, er ging iets mis.', 'bot');
                    }
                })
                .catch(() => {
                    clearTimeout(timeoutId);
                    hideTyping();
                    enableForm();
                    appendMessage('Sorry, er ging iets mis.', 'bot');
                });

            textarea.value = '';
        });

    }

    // Expose for potential external use
    window.samChatToggle = { open: openChat, close: closeChat, toggle: toggleChat, isOpen };
})();